import { Middleware, MiddlewareAPI, Dispatch, AnyAction } from 'redux';
import ReactGA from 'react-ga';

import {
  ANALYTICS_ACTIONS,
  API_GROUPS,
  DEV_KEY,
  PRODUCTION_KEY,
  TIMING_EVENT,
} from 'appConstants';
import { AppState } from 'reducers';

interface ExtendedMiddleware<StateType> extends Middleware {
  <S extends StateType>(api: MiddlewareAPI<S>): (
    next: Dispatch<S>,
  ) => Dispatch<S>;
}

const hasDedicated =
  window.PORTAL_DATA &&
  window.PORTAL_DATA.tenants.some((tenant: string) => {
    return tenant.includes('hybrid:');
  });

// initialize google analytics with tracking id
ReactGA.initialize(
  /localhost:|staging./g.test(window.location.href) ? DEV_KEY : PRODUCTION_KEY,
  { gaOptions: { siteSpeedSampleRate: 100 } },
);

ReactGA.set({ dimension1: hasDedicated ? 'Dedicated' : 'Cloud' });

const analyticsMiddleware: ExtendedMiddleware<AppState> = <S extends AppState>(
  api: MiddlewareAPI<S>,
) => (next: Dispatch<S>) => <A extends AnyAction>(action: A): A => {
  const actionType = action.type;
  const isTimingEvent = actionType === TIMING_EVENT;
  const matchAction = ANALYTICS_ACTIONS[actionType];

  if (!isTimingEvent && !matchAction) {
    return next(action);
  }

  if (matchAction) {
    const state = api.getState();
    const ticketId = state.selectedTicket.ticketId;

    // classify the event category
    matchAction.label =
      window.location && window.location.pathname.includes('create')
        ? 'New Ticket'
        : ticketId;

    matchAction.action = action.type;
    try {
      ReactGA.event(matchAction);
    } catch (err) {
      console.error('error sending ga logs : ', err.message);
    }
  }

  if (isTimingEvent) {
    const apiIndex = API_GROUPS.find((key) => {
      return action.url.includes(key.url);
    });

    if (apiIndex) {
      try {
        ReactGA.timing({
          category: apiIndex.category,
          variable: `${action.method} ${action.url.replace(apiIndex.url, '')}`,
          value: action.time,
          label: action.url,
        });
      } catch (err) {
        console.error('error sending ga timing logs : ', err.message);
      }
    }
  }

  return next(action);
};

export { analyticsMiddleware };
